import { useEffect, useRef } from 'react';
import { AppState, AppStateStatus, Platform } from 'react-native';

interface UseAppStateProps {
  onBackground?: () => void;
  onForeground?: () => void;
  debugTag?: string; // Optional: enable logging with a tag when provided
}

export const useAppState = ({ onBackground, onForeground, debugTag }: UseAppStateProps) => {
  const initialStable: AppStateStatus = Platform.OS === 'web'
    ? (((globalThis as any)?.document?.visibilityState === 'hidden') ? 'background' : 'active')
    : (AppState.currentState === 'background' ? 'background' : 'active');
  const appState = useRef<AppStateStatus>(Platform.OS === 'web' ? 'active' : AppState.currentState);
  // Track a stable app state that ignores 'inactive' transitions
  const stableState = useRef<AppStateStatus>(initialStable);

  useEffect(() => {
    if (Platform.OS === 'web') {
      const doc: any = (globalThis as any)?.document;
      if (!doc) return;

      const handleVisibilityChange = () => {
        const isVisible = doc.visibilityState !== 'hidden';
        if (__DEV__ && debugTag) {
          console.log(`[AppState:${debugTag}] visibility changed to`, doc.visibilityState);
        }
        if (isVisible) {
          if (stableState.current !== 'active') {
            if (__DEV__ && debugTag) {
              console.log(`[AppState:${debugTag}] App came to foreground (web)`);
            }
            onForeground?.();
            stableState.current = 'active';
          }
        } else {
          if (stableState.current !== 'background') {
            if (__DEV__ && debugTag) {
              console.log(`[AppState:${debugTag}] App went to background (web)`);
            }
            onBackground?.();
            stableState.current = 'background';
          }
        }
      };

      doc.addEventListener('visibilitychange', handleVisibilityChange);

      return () => {
        doc.removeEventListener('visibilitychange', handleVisibilityChange);
      };
    }

    const handleAppStateChange = (nextAppState: AppStateStatus) => {
      if (__DEV__ && debugTag) {
        console.log(`[AppState:${debugTag}] changed from`, appState.current, 'to', nextAppState);
      }

      // Consider only stable states: 'active' and 'background'
      if (nextAppState === 'background') {
        if (stableState.current !== 'background') {
          if (__DEV__ && debugTag) {
            console.log(`[AppState:${debugTag}] App went to background`);
          }
          onBackground?.();
          stableState.current = 'background';
        }
      } else if (nextAppState === 'active') {
        if (stableState.current !== 'active') {
          if (__DEV__ && debugTag) {
            console.log(`[AppState:${debugTag}] App came to foreground`);
          }
          onForeground?.();
          stableState.current = 'active';
        }
      }

      appState.current = nextAppState;
    };

    const subscription = AppState.addEventListener('change', handleAppStateChange);

    return () => subscription?.remove();
  }, [onBackground, onForeground, debugTag]);

  return {
    currentState: appState.current
  };
};
